/**
 * =============================================================================
 * IDS CRM Web - Confirm my hours (a co-engineer's own entry on a job or ticket)
 * =============================================================================
 *
 * Multi-engineer jobs, Phase 4 (Gareth's 11 Sep crew rules). When the lead
 * logs work on a job and names other engineers, each of them gets their OWN
 * work log with the lead's times, left open. This is where they check it:
 * keep the times or correct their own finish, add a note, and tick that the
 * hours are right. Ticking signs the entry off, after which it is locked like
 * any other closed work log.
 *
 * Deliberately narrow: start, finish, notes and the confirm tick only. The
 * server is the authority - it refuses a closed log, someone else's log and a
 * paid pay period, and this shows its sentence as it is. On a job it saves to
 * PATCH /api/v1/tasks/:id/work-log/:logId (the default); a ticket passes its
 * own endpoint, PATCH /api/v1/tickets/:ticketId/work-log/:logId/hours
 * (Phase 6, step 0f), and workLabel="ticket" for the explanation line.
 * =============================================================================
 */

import React, { useState } from 'react';
import { formatTime, toLocalDateTimeInput } from '../utils/format';
import { ukTodayKey } from '../utils/dates';

const validDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Saved Log Work (Phase 6 step 7, Gareth's answer to question 1): a crew
 * entry keeps the filer's times, and the engineer's own Job Stages stay
 * unlinked. The engineer's own journeys on the entry's UK day that have a
 * start and a later stamp are offered as "Use my Job Stages times (07:50 to
 * 17:40)", which only fills the two boxes - they still confirm. Newest last,
 * each journey once.
 */
export function jobStagesChoices(journeys, log) {
  const entryStart = validDate(log && log.started_at);
  if (!entryStart) return [];
  const entryDay = ukTodayKey(entryStart);
  const seen = new Set();
  return (Array.isArray(journeys) ? journeys : [])
    .map(journey => {
      const start = validDate(journey && journey.departed_at);
      const end = validDate(journey && (journey.returned_at || journey.departed_site_at || journey.arrived_at));
      return { id: journey && journey.travel_id, start, end };
    })
    .filter(choice => choice.start && choice.end && choice.end > choice.start && ukTodayKey(choice.start) === entryDay)
    .filter(choice => {
      const key = String(choice.id || `${choice.start.toISOString()}|${choice.end.toISOString()}`);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.start - b.start);
}

/**
 * @param {object} props
 * @param {string} [props.taskId] - the job the log belongs to (used when no endpoint is given)
 * @param {string} [props.endpoint] - where to PATCH the hours; defaults to the job route
 * @param {string} [props.workLabel] - "job" (default) or "ticket", for the explanation line
 * @param {object} props.log - the work log being confirmed (log_id, started_at, ended_at, notes)
 * @param {string} [props.leadName] - who logged the crew, for the explanation line
 * @param {Function} props.authFetch
 * @param {Function} props.onClose
 * @param {(payrollNotice: ?string) => void} props.onSaved - called after a save, with the
 *        server's payroll_notice (or null) so the page can show it
 * @param {Array<object>} [props.journeys] - the engineer's own Job Stages records on this job
 *        (travel_id, departed_at, arrived_at, departed_site_at, returned_at). One on the
 *        entry's UK day is offered as "Use my Job Stages times". Without it, nothing changes.
 */
function ConfirmHoursModal({ taskId, endpoint, workLabel = 'job', log, leadName, authFetch, onClose, onSaved, journeys = null }) {
  const initialStart = log?.started_at ? toLocalDateTimeInput(log.started_at) : '';
  const initialFinish = log?.ended_at ? toLocalDateTimeInput(log.ended_at) : '';
  const initialNotes = log?.notes || '';

  const [startedAt, setStartedAt] = useState(initialStart);
  const [endedAt, setEndedAt] = useState(initialFinish);
  const [notes, setNotes] = useState(initialNotes);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const stageChoices = jobStagesChoices(journeys, log);

  const applyStageTimes = (choice) => {
    setStartedAt(toLocalDateTimeInput(choice.start));
    setEndedAt(toLocalDateTimeInput(choice.end));
    setError(null);
  };

  const submit = async (event) => {
    event.preventDefault();
    setError(null);

    if (!startedAt) {
      setError('Enter the time you started.');
      return;
    }
    if (!endedAt) {
      setError('Enter the time you finished.');
      return;
    }
    const start = new Date(startedAt);
    const finish = new Date(endedAt);
    if (Number.isNaN(start.getTime()) || Number.isNaN(finish.getTime())) {
      setError('Enter the start and finish as a date and time.');
      return;
    }
    if (finish <= start) {
      setError('Finish time must be after the start time.');
      return;
    }
    if (!confirmed) {
      setError('Tick that these hours are right before saving.');
      return;
    }

    // Only what changed is sent, so an untouched time is not re-saved (the
    // inputs hold whole minutes and would otherwise read as a change).
    const body = { confirm: true };
    if (startedAt !== initialStart) body.started_at = start.toISOString();
    if (endedAt !== initialFinish || !log?.ended_at) body.ended_at = finish.toISOString();
    if (notes !== initialNotes) body.notes = notes;

    setSaving(true);
    try {
      const response = await authFetch(endpoint || `/api/v1/tasks/${taskId}/work-log/${log.log_id}`, {
        method: 'PATCH',
        body: JSON.stringify(body)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Your hours could not be saved. Try again, or ask a manager.');
      }
      onSaved(data.payroll_notice || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content confirm-hours-modal" onClick={event => event.stopPropagation()} style={{ maxWidth: '520px' }}>
        <div className="modal-header">
          <h2>Confirm my hours</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            {error && <div className="modal-error" role="alert">{error}</div>}
            <p className="modal-hint">
              {leadName ? `${leadName} logged you on this ${workLabel}` : `You were logged on this ${workLabel}`} with these times.
              {' '}Correct them if you started or finished at a different time, then confirm. Once confirmed,
              {' '}your hours are signed off and locked.
            </p>
            {stageChoices.length > 0 && (
              <div className="form-group confirm-hours-stage-times">
                {stageChoices.map(choice => (
                  <button
                    key={`${choice.id || ''}${choice.start.toISOString()}`}
                    type="button"
                    className="btn-small"
                    onClick={() => applyStageTimes(choice)}
                  >
                    Use my Job Stages times ({formatTime(choice.start)} to {formatTime(choice.end)})
                  </button>
                ))}
                <span className="field-help">Fills in the two times below. Check them, then confirm.</span>
              </div>
            )}
            <div className="form-group">
              <label htmlFor="confirm_started_at">Start time *</label>
              <input
                id="confirm_started_at"
                type="datetime-local"
                value={startedAt}
                onChange={event => setStartedAt(event.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirm_ended_at">Finish time *</label>
              <input
                id="confirm_ended_at"
                type="datetime-local"
                value={endedAt}
                onChange={event => setEndedAt(event.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirm_notes">Notes</label>
              <textarea
                id="confirm_notes"
                rows={3}
                value={notes}
                onChange={event => setNotes(event.target.value)}
              />
            </div>
            <div className="form-group confirm-hours-tick">
              <label htmlFor="confirm_hours_right">
                <input
                  id="confirm_hours_right"
                  type="checkbox"
                  checked={confirmed}
                  onChange={event => setConfirmed(event.target.checked)}
                />
                {' '}These hours are right
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-save" disabled={saving || !confirmed}>
              {saving ? 'Saving...' : 'Confirm my hours'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ConfirmHoursModal;
